const { SYSLOG_SEVERITY_LEVEL, SYSLOG_FACILITY } = require('./constants');

class Logger {
    /**
     * When creating a new logger, the context object does not have to be complete.
     * It will be merged with defaults and any calls to setContext will be merged.
     * Will also be merged with any called to logging methods which include a context object
     *
     * `options.remoteURL` is required or this will throw an error.
     *
     * Logger automatically hooks up to onbeforeunload/onunload event to flush logs if the user leaves the page.
     *
     * Otherwise logs are sent in batches based on the timing specified by `options.bufferTimer`.
     * @param {object} context
     * @param {object} options
     */
    constructor(context, options = {}) {
        this._context = this.__mergeContext(
            {
                syslog: {
                    facility: SYSLOG_FACILITY.LOCAL_0,
                },
            },
            context || { syslog: {}, correlation: {}, customer: {}, ULS: {} }
        );

        // Can't send logs to remote if there is no remote!
        if (!options.remoteURL) {
            throw new Error(
                'Logger Error: options.remoteURL is required to be able to send logs to remote endpoint!'
            );
        }

        this._options = {
            bufferTimer: options.bufferTimer || 5000,
            maxMessageLength: options.maxMessageLength || 1000,
            remoteURL: options.remoteURL,
        };

        // Create a buffer of logs. These are sent every x milliseconds to the remote.
        this._buffer = [];
        this._timer = setInterval(
            this.__sendLogsFromBuffer.bind(this),
            this._options.bufferTimer
        );

        // If the user leaves the page for any reason (navigating, refresh, browser close, etc)...
        // ... force the logs to send immediately.
        //
        // We have both onbeforeunload and onunload with a check of onbeforeunload because some browsers have one...
        // ... or the other and some have both. This prevents it from being called twice.
        let hasEmptiedBuffer = false;
        window.onbeforeunload = window.onunload = () => {
            if (!hasEmptiedBuffer) {
                hasEmptiedBuffer = true;
                this.__sendLogsFromBuffer();
            }
        };
    }

    /**
     * Given a context object, will merge it with the existing context of the logger
     * @param {object} context
     */
    setContext(context) {
        this._context = this.__mergeContext(this._context, context);
    }

    /**
     * Get the context object
     */
    getContext() {
        return this._context;
    }

    /**
     * Creates a new logger based on the context of this logger.
     * Merges any passed in context with the existing logger's context.
     * @param {object} context
     */
    cloneLogger(context = {}) {
        return new Logger(
            this.__mergeContext(this._context, context),
            this._options
        );
    }

    /**
     * Generates a random GUID to be used as a transaction ID
     * @param {*} a - DO NOT PROVIDE THIS. This param is used then this function is called recursively.
     */
    generateGuid(a) {
        return a
            ? (a ^ ((Math.random() * 16) >> (a / 4))).toString(16)
            : ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(
                  /[018]/g,
                  this.generateGuid
              );
    }

    /**
     * Logs a Debug message.
     * Context is optional but if provided will be merged with existing logger context.
     * @param {string} message
     * @param {object} context
     */
    debug(message, context) {
        this.__writelog(SYSLOG_SEVERITY_LEVEL.DEBUG, message, context);
    }

    /**
     * Logs an Informational message.
     * Context is optional but if provided will be merged with existing logger context.
     * @param {string} message
     * @param {object} context
     */
    info(message, context) {
        this.__writelog(SYSLOG_SEVERITY_LEVEL.INFORMATIONAL, message, context);
    }

    /**
     * Logs a Warning message.
     * Context is optional but if provided will be merged with existing logger context.
     * @param {string} message
     * @param {object} context
     */
    warn(message, context) {
        this.__writelog(SYSLOG_SEVERITY_LEVEL.WARNING, message, context);
    }

    /**
     * Logs an Error message.
     * Context is optional but if provided will be merged with existing logger context.
     * @param {string} message
     * @param {object} context
     */
    error(message, context) {
        this.__writelog(SYSLOG_SEVERITY_LEVEL.ERROR, message, context);
    }

    /**
     * Logs a Critical message.
     * Context is optional but if provided will be merged with existing logger context.
     * @param {string} message
     * @param {object} context
     */
    critical(message, context) {
        this.__writelog(SYSLOG_SEVERITY_LEVEL.CRITICAL, message, context);
    }

    /**
     * INTERNAL METHOD.
     *
     * Writes a message to the console or the buffer. Depending on the environment will have different behaviour:
     *    development: Simply log format with severity string in square brackets
     *    production: Put the log message into the buffer to be sent to the server
     *
     * Context is optional but if provided will be merged with existing logger context.
     * @param {number} severity
     * @param {string} message
     * @param {object} context
     */
    __writelog(severity, message, context = {}) {
        const ENVIRONMENT = process.env.NODE_ENV || 'development';
        const mergedContext = this.__mergeContext(this._context, context);

        // If a transactionID is not provided, lets generate one
        if (!mergedContext.correlation.transactionId) {
            mergedContext.correlation.transactionId = this.generateGuid();
        }

        if (ENVIRONMENT === 'development') {
            console.log(`[${this.__getSeverityString(severity)}] ${message}`);
        } else {
            // Truncate messages to 1000 characters to prevent issues with message size being ...
            // ... too big for the server to handle.
            const truncatedMessage =
                message.length > this._options.maxMessageLength
                    ? message.substring(0, this._options.maxMessageLength) +
                      ' [MESSAGE TRUNCATED]'
                    : message;

            this._buffer.push({
                severity,
                message: truncatedMessage,
                context: mergedContext,
            });
        }
    }

    /**
     * Takes logs from the buffer and sends them to the remote.
     *
     * An implementation of `fetch` is required on the global scope which should be the case in most SPAs.
     *
     * If there is a problem sending the logs, it will add them back to the buffer and try again later.
     */
    async __sendLogsFromBuffer() {
        const logsToSend = [];
        // Cache the length so that we only send logs that were in the buffer when this method was called
        const bufferLength = this._buffer.length;
        for (let i = 0; i < bufferLength; i++) {
            // Remove the item from the first of the buffer to be send to the remote
            logsToSend.push(this._buffer.shift());
        }

        // Send logs to remote
        if (logsToSend.length > 0) {
            return fetch(this._options.remoteURL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    logs: logsToSend,
                }),
            }).catch(() => {
                // If sending logs fails, add them back to the buffer and try again later
                this._buffer = this._buffer.concat(logsToSend);
                throw new Error('Failed to send logs. Adding back to buffer.');
            });
        }
    }

    /**
     * Given two context objects, merges them together into one object.
     * This method does a "deep" copy.
     * @param {object} context1
     * @param {object} context2
     */
    __mergeContext(context1, context2) {
        return {
            ...context1,
            ...context2,
            syslog: {
                ...context1.syslog,
                ...(context2.syslog || {}),
            },
            correlation: {
                ...context1.correlation,
                ...(context2.correlation || {}),
            },
            customer: {
                ...context1.customer,
                ...(context2.customer || {}),
            },
            ULS: {
                ...context1.ULS,
                ...(context2.ULS || {}),
            },
        };
    }

    /**
     * Gets the text version of severity given a severity number
     * @param {number} severity
     */
    __getSeverityString(severity) {
        return Object.keys(SYSLOG_SEVERITY_LEVEL)
            .find((s) => SYSLOG_SEVERITY_LEVEL[s] === severity)
            .toLowerCase();
    }
}

module.exports = Logger;
